import { useMemo, useState } from 'react';
import { navCategories, navEntries } from '@/mocks/nav';
import type { NavEntry } from '@/pages/home/types';
import EntryModal from '@/pages/home/components/EntryModal';

interface EntryExplorerProps {
  keyword: string;
  setKeyword: (keyword: string) => void;
  activeCategory: string;
  setActiveCategory: (category: string) => void;
}

const categoryNames: Record<string, string> = navCategories.reduce<Record<string, string>>((acc, item) => {
  acc[item.id] = item.name;
  return acc;
}, {});

const allFilters = [{ id: 'all', name: '全部入口', icon: 'ri-apps-2-line' }].concat(
  navCategories.map((item) => ({ id: item.id, name: item.name, icon: item.icon })),
);

export default function EntryExplorer({
  keyword,
  setKeyword,
  activeCategory,
  setActiveCategory,
}: EntryExplorerProps) {
  const [selected, setSelected] = useState<NavEntry | null>(null);
  const [copiedId, setCopiedId] = useState('');

  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    return navEntries.filter((entry) => {
      const matchCategory = activeCategory === 'all' || entry.categoryId === activeCategory;
      if (!matchCategory) return false;
      if (!key) return true;
      const haystack = [entry.name, entry.desc, categoryNames[entry.categoryId] || '', ...entry.tags]
        .join(' ')
        .toLowerCase();
      return haystack.includes(key);
    });
  }, [keyword, activeCategory]);

  const handleCopy = async (entry: NavEntry) => {
    try {
      await navigator.clipboard.writeText(entry.addr);
    } catch {
      const input = document.createElement('textarea');
      input.value = entry.addr;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setCopiedId(entry.id);
    window.setTimeout(() => setCopiedId(''), 2000);
  };

  return (
    <section id="hot" className="scroll-mt-20 border-b border-background-200 bg-background-50 py-14 md:scroll-mt-24 md:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-primary-600 md:text-sm">
              <i className="ri-flashlight-line text-base" aria-hidden="true"></i>
              Hot Entries
            </p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              热门直达入口
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              按主题分类浏览，或直接搜索关键词。点开卡片可查看站点简介并一键复制直达地址。
            </p>
          </div>
          <p className="text-sm text-foreground-500">
            共 <span className="font-heading text-lg font-bold text-primary-600">{filtered.length}</span> 个入口符合条件
          </p>
        </div>

        <div className="mt-8 flex flex-col gap-4">
          <div className="relative w-full max-w-xl">
            <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" aria-hidden="true"></i>
            </span>
            <label htmlFor="entry-search" className="sr-only">
              搜索入口
            </label>
            <input
              id="entry-search"
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索入口名称、标签或关键词"
              className="w-full rounded-md border border-background-300 bg-background-100 py-3 pl-11 pr-4 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:ring-2 focus:ring-primary-400/30"
            />
            {keyword && (
              <button
                type="button"
                onClick={() => setKeyword('')}
                aria-label="清除搜索关键词"
                className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-lg text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-950"
              >
                <i className="ri-close-line" aria-hidden="true"></i>
              </button>
            )}
          </div>

          <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            {allFilters.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveCategory(item.id)}
                className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-2 text-sm transition-colors ${
                  activeCategory === item.id
                    ? 'border-primary-500 bg-primary-500 text-background-50'
                    : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-foreground-950'
                }`}
              >
                <i className={item.icon} aria-hidden="true"></i>
                {item.name}
              </button>
            ))}
          </div>
        </div>

        {filtered.length > 0 ? (
          <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
            {filtered.map((entry) => (
              <li key={entry.id}>
                <article className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:border-primary-300">
                  <div className="flex items-start justify-between gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-50 text-lg text-primary-600">
                      <i className="ri-links-line" aria-hidden="true"></i>
                    </span>
                    <div className="flex items-center gap-2">
                      {entry.badge && (
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-[11px] ${
                            entry.badge === '热门'
                              ? 'bg-accent-100 text-accent-900'
                              : entry.badge === '新收录'
                                ? 'bg-secondary-100 text-secondary-900'
                                : 'bg-primary-100 text-primary-700'
                          }`}
                        >
                          {entry.badge}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-fire-line text-accent-600" aria-hidden="true"></i>
                        {entry.hot.toLocaleString('zh-CN')}
                      </span>
                    </div>
                  </div>

                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{entry.name}</h3>
                  <p className="mt-1 text-xs text-foreground-500">{categoryNames[entry.categoryId]}</p>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{entry.desc}</p>

                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {entry.tags.map((tag) => (
                      <span key={tag} className="rounded bg-background-200/80 px-2 py-0.5 text-[11px] text-foreground-600">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setSelected(entry)}
                    className="mt-4 inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-500 hover:text-background-50"
                  >
                    查看入口
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </button>
                </article>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
              <i className="ri-search-eye-line" aria-hidden="true"></i>
            </span>
            <p className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到匹配的入口</p>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              换个关键词试试，或点击下方按钮查看全部收录入口。也可以到页面底部提交你想找的入口。
            </p>
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                setActiveCategory('all');
              }}
              className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line" aria-hidden="true"></i>
              重置筛选条件
            </button>
          </div>
        )}
      </div>

      {selected && (
        <EntryModal
          entry={selected}
          categoryName={categoryNames[selected.categoryId] || ''}
          copied={copiedId === selected.id}
          onCopy={handleCopy}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}